import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { dashboardAPI, chatbotAPI, authAPI, presenceAPI, chatAPI, paymentAPI, handleAPIError, getStatusColor, formatDate } from '../services/api';
import { getPlanIdToNameMap } from '../config/stripe';
import ChatbotCreator from '../components/ChatbotCreator';
import SimpleChatSystem from '../components/SimpleChatSystem';
import ChatbotDetailsModal from '../components/ChatbotDetailsModal';
import PaymentModal from '../components/PaymentModal';
import PlanSelectionModal from '../components/PlanSelectionModal';
import DeploymentModal from '../components/DeploymentModal';
import CustomerTestingModal from '../components/CustomerTestingModal';
import EmployeeManagementModal from '../components/EmployeeManagementModal';
import ChatbotPhaseRoadmap from '../components/ChatbotPhaseRoadmap';
import SEOHead from '../components/SEOHead';
import { 
  Plus,
  Bot, 
  BarChart3, 
  Settings, 
  Users, 
  TrendingUp, 
  Clock,
  CheckCircle,
  AlertCircle,
  Crown,
  Activity,
  MessageCircle,
  Building,
  UserCheck,
  Globe,
  Zap,
  Star,
  Shield,
  Sparkles,
  Eye,
  Calendar,
  DollarSign,
  Briefcase,
  Send,
  UserPlus,
  Monitor,
  RefreshCw,
  TestTube,
  CreditCard,
  Rocket,
  MoreVertical,
  Trash2,
  Code,
  Edit,
  X
} from 'lucide-react';

const Dashboard = () => {
  const { user, isAdmin, isEmployee, isCustomer, isDemo, canCreateChatbots, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Plan ID to Name mapping for human-readable plan names
  const [planIdToName, setPlanIdToName] = useState({});

  // Load plan ID to name mapping
  useEffect(() => {
    const loadPlanMapping = async () => {
      try {
        const mapping = await getPlanIdToNameMap();
        setPlanIdToName(mapping);
      } catch (error) {
        console.error('Error loading plan ID mapping:', error);
        // Fallback to empty object
        setPlanIdToName({});
      }
    };
    
    loadPlanMapping();
  }, []);

  const [stats, setStats] = useState({});
  const [chatbots, setChatbots] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [showChatbotCreator, setShowChatbotCreator] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showEmployeeManagement, setShowEmployeeManagement] = useState(false);
  const [showChatbotDetails, setShowChatbotDetails] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentChatbot, setPaymentChatbot] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // User management state
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [allUsersData, setAllUsersData] = useState([]);
  
  // Phase workflow modal states
  const [showCustomerTesting, setShowCustomerTesting] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [showDeployment, setShowDeployment] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [chatbotsPerPage] = useState(10);
  const [showAllChatbots, setShowAllChatbots] = useState(false);

  // Check authentication and redirect if not authenticated
  useEffect(() => {
    // Wait for auth to initialize before doing redirects
    if (authLoading) {
      return;
    }
    
    // Redirect unauthenticated users to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Redirect unverified users to email verification
    if (user && !user.is_verified) {
      navigate(`/email-verification?email=${encodeURIComponent(user.email)}`);
      return;
    }
  }, [isAuthenticated, user, navigate, authLoading]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        console.log('Fetching dashboard data for user:', user?.email, 'role:', user?.role);

        // Fetch stats
        try {
          const statsResponse = await dashboardAPI.getStats();
          console.log('Stats response:', statsResponse.data);
          setStats(statsResponse.data);
        } catch (statsError) {
          console.warn('Failed to fetch stats:', statsError);
          // Continue with default/empty stats
        }

        // Fetch chatbots
        try {
          const chatbotsResponse = await dashboardAPI.getMyChatbots();
          console.log('Chatbots response:', chatbotsResponse.data);
          setChatbots(chatbotsResponse.data || []);
        } catch (chatbotError) {
          console.warn('Failed to fetch chatbots:', chatbotError);
          setChatbots([]);
        }

        // Fetch recent activity
        try {
          const activityResponse = await dashboardAPI.getRecentActivity(10);
          setRecentActivity(activityResponse.data.activities || []);
        } catch (activityError) {
          console.warn('Failed to fetch recent activity:', activityError);
          setRecentActivity([]);
        }
        
        // Fetch unread message count
        try {
          const unreadResponse = await chatAPI.getTotalUnreadCount();
          setUnreadMessageCount(unreadResponse.data.unread_count || 0);
        } catch (unreadError) {
          console.warn('Failed to fetch unread count:', unreadError);
          setUnreadMessageCount(0);
        }

        // Fetch employees if admin
        if (isAdmin()) {
          try {
            const employeesResponse = await authAPI.getEmployees();
            setEmployees(employeesResponse.data);
          } catch (employeeError) {
            console.warn('Failed to fetch employees:', employeeError);
            setEmployees([]); // Set empty array as fallback
          }
        }

        // Check user deposit status
        // No longer needed - we check per chatbot now

      } catch (error) {
        console.error('Dashboard data fetch error:', error);
        const errorMessage = handleAPIError(error, 'Failed to load dashboard data');
        toast({
          title: "Loading Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user, isAdmin, toast]);

  // Set up periodic online users fetching
  useEffect(() => {
    if (user && isAdmin()) {
      // Initial fetch
      fetchOnlineUsers();
      
      // Set up periodic fetching every 30 seconds
      const interval = setInterval(fetchOnlineUsers, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user, isAdmin]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show a one-time banner if redirected after email update
  useEffect(() => {
    if (user) {
      const state = window.history.state && window.history.state.usr;
      if (state && state.emailUpdated) {
        toast({
          title: 'Email updated',
          description: `Your login email was changed to ${state.newEmail}.`,
        });
        // Clear state so it doesn't show on refresh
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [user, toast]);

  // Handle payment success/cancel URL parameters
  useEffect(() => {
    const handlePaymentURLParams = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      
      // Handle chatbot creation success
      if (urlParams.get('chatbot_creation_success') === 'true') {
        const sessionId = urlParams.get('session_id');
        
        try {
          // Get stored chatbot creation data from sessionStorage
          const storedData = sessionStorage.getItem('chatbot_creation_data');
          
          if (storedData) {
            const chatbotData = JSON.parse(storedData);
            
            // First try to complete chatbot creation with stored form data
            try {
              const response = await paymentAPI.completeChatbotCreationAfterPayment(chatbotData);
              
              if (response.data.success) {
                toast({
                  title: "Chatbot Created Successfully! 🎉",
                  description: response.data.already_exists ? 
                    "Your chatbot was already created after payment." :
                    "Your chatbot has been created and activated with all your custom settings.",
                });
                
                // Clear the stored data
                sessionStorage.removeItem('chatbot_creation_data');
              } else {
                throw new Error('Failed to complete chatbot creation');
              }
            } catch (completionError) {
              console.warn('Primary completion failed, trying fallback...', completionError);
              
              // Fallback: Wait for webhook processing (in case of delays)
              setTimeout(async () => {
                try {
                  await refreshDashboardData();
                  const chatbotsResponse = await dashboardAPI.getMyChatbots();
                  const recentChatbots = chatbotsResponse.data.filter(cb => 
                    cb.company_name === chatbotData.company_name && 
                    cb.deposit_paid === true
                  );
                  
                  if (recentChatbots.length > 0) {
                    toast({
                      title: "Chatbot Created Successfully! 🎉",
                      description: "Your chatbot was created via payment processing. You can see it in your dashboard.",
                    });
                    sessionStorage.removeItem('chatbot_creation_data');
                  } else {
                    // Still no chatbot found - inform user
                    toast({
                      title: "Payment Processed - Chatbot Pending",
                      description: "Your payment was successful! Your chatbot is being set up and will appear shortly. Please refresh in a moment or contact support if needed.",
                      variant: "default",
                    });
                  }
                } catch (fallbackError) {
                  console.error('Fallback completion also failed:', fallbackError);
                  toast({
                    title: "Payment Successful - Please Contact Support",
                    description: "Your payment was processed successfully, but we need to manually complete your chatbot setup. Our team will contact you shortly.",
                    variant: "default",
                  });
                }
              }, 5000); // Wait 5 seconds for webhook
              
              // Show immediate message
              toast({
                title: "Payment Processing...",
                description: "Your payment was successful! We're setting up your chatbot now - it will appear shortly.",
              });
            }
          } else {
            // No stored data - rely on webhook (legacy flow)
            setTimeout(async () => {
              toast({
                title: "Chatbot Created Successfully! 🎉",
                description: "Your chatbot has been created and activated. You can see it in your dashboard.",
              });
              await refreshDashboardData();
            }, 3000);
          }
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          
        } catch (error) {
          console.error('Error completing chatbot creation:', error);
          const errorMessage = handleAPIError(error, 'Failed to complete chatbot creation');
          
          // Enhanced error handling with more helpful messages
          let userFriendlyMessage = errorMessage;
          if (errorMessage.includes('No valid payment found') || errorMessage.includes('No valid deposit payment found')) {
            userFriendlyMessage = "We're having trouble verifying your payment. This sometimes happens due to processing delays. Please wait a moment and refresh the page, or contact support if the issue persists.";
          }
          
          toast({
            title: "Chatbot Creation Issue",
            description: userFriendlyMessage,
            variant: "destructive",
            duration: 10000, // Show longer for payment issues
          });
          
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Still refresh dashboard data in case something was created
          await refreshDashboardData();
        }
      }
      
      // Handle chatbot creation cancellation
      if (urlParams.get('chatbot_creation_cancelled') === 'true') {
        toast({
          title: "Chatbot Creation Cancelled",
          description: "Your chatbot creation was cancelled. You can try again when ready.",
          variant: "destructive",
        });
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      // Handle final payment success
      if (urlParams.get('final_payment_success') === 'true') {
        const sessionId = urlParams.get('session_id');
        
        // Give the webhook a moment to process
        setTimeout(async () => {
          toast({
            title: "Final Payment Successful! 🎉",
            description: "Your setup fee has been paid. You can now select your subscription plan.",
          });
          
          // Clear URL parameters and refresh dashboard data
          window.history.replaceState({}, document.title, window.location.pathname);
          await refreshDashboardData();
          
          // Check if user has any chatbots in final_payment status to auto-open plan selection
          const chatbotsResponse = await dashboardAPI.getMyChatbots();
          const finalPaymentChatbots = chatbotsResponse.data.filter(cb => cb.status === 'final_payment');
          if (finalPaymentChatbots.length > 0) {
            setSelectedChatbot(finalPaymentChatbots[0]);
            setShowPlanSelection(true);
          }
        }, 2000); // Wait 2 seconds for webhook to process
      }
      
      // Handle final payment cancellation
      if (urlParams.get('final_payment_cancelled') === 'true') {
        toast({
          title: "Payment Cancelled",
          description: "Your final payment was cancelled. You can try again when ready.",
          variant: "destructive",
        });
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      // Handle plan selection success
      if (urlParams.get('plan_selection_success') === 'true') {
        const planId = urlParams.get('plan_id');
        const sessionId = urlParams.get('session_id');
        
        setTimeout(async () => {
          toast({
            title: "Subscription Active! 🚀",
            description: "Your subscription plan is now active with a 30-day free trial. You can deploy your chatbot!",
          });
          
          // Clear URL parameters and refresh dashboard data
          window.history.replaceState({}, document.title, window.location.pathname);
          await refreshDashboardData();
        }, 2000); // Wait for webhook to process
      }
      
      // Handle plan selection cancellation
      if (urlParams.get('plan_selection_cancelled') === 'true') {
        toast({
          title: "Plan Selection Cancelled",
          description: "You can select a subscription plan later from your chatbot details.",
          variant: "destructive",
        });
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handlePaymentURLParams();
  }, []);

  // Auto-load user data when showing user management
  useEffect(() => {
    if (showUserManagement && isAdmin() && allUsersData.length === 0) {
      loadAllUsersData();
    }
  }, [showUserManagement, isAdmin]);

  const handleChatbotCreated = async (chatbot) => {
    console.log('Chatbot created successfully:', chatbot);
    setShowChatbotCreator(false);
    
    // For customers, show payment modal immediately after creation
    if (isCustomer() && !chatbot.deposit_paid) {
      setPaymentChatbot(chatbot);
      setShowPaymentModal(true);
      
      toast({
        title: "Chatbot created successfully!",
        description: `${chatbot.company_name} chatbot created. Please pay the $200 deposit to activate it.`,
      });
    } else {
      // For demo users or if already paid, just show success
      toast({
        title: "Chatbot created successfully!",
        description: `${chatbot.company_name} chatbot has been created and is ready for review.`,
      });
    }

    try {
      // Refresh stats and chatbots from server to ensure consistency
      console.log('Refreshing dashboard data after chatbot creation...');
      await refreshDashboardData();
      console.log('Dashboard data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing dashboard after chatbot creation:', error);
      // Don't show error toast since the chatbot was created successfully
      // Just log the error for debugging
    }
  };

  const handlePaymentSuccess = async (chatbotId) => {
    console.log('Payment successful for chatbot:', chatbotId);
    
    // Refresh dashboard data to show updated payment status
    await refreshDashboardData();
    
    toast({
      title: "Payment successful!",
      description: "Your chatbot is now active and ready for development!",
    });
    
    setShowPaymentModal(false);
    setPaymentChatbot(null);
  };

  const handleShowPaymentModal = (chatbot) => {
    setPaymentChatbot(chatbot);
    setShowPaymentModal(true);
  };

  const refreshUnreadCount = async () => {
    try {
      const unreadResponse = await chatAPI.getTotalUnreadCount();
      setUnreadMessageCount(unreadResponse.data.unread_count || 0);
    } catch (error) {
      console.warn('Failed to refresh unread count:', error);
    }
  };

  const refreshDashboardData = async () => {
    try {
      console.log('Refreshing dashboard data...');
      
      // Refresh stats
      const statsResponse = await dashboardAPI.getStats();
      setStats(statsResponse.data);

      // Refresh chatbots
      const chatbotsResponse = await dashboardAPI.getMyChatbots();
      console.log('Refreshed chatbots:', chatbotsResponse.data);
      setChatbots(chatbotsResponse.data || []);

      // Refresh recent activity
      const activityResponse = await dashboardAPI.getRecentActivity(10);
      setRecentActivity(activityResponse.data.activities || []);

      // Refresh employees list if admin
      if (isAdmin()) {
        console.log('Refreshing employees list for admin...');
        const employeesResponse = await authAPI.getEmployees();
        setEmployees(employeesResponse.data);
        console.log('Employees refreshed:', employeesResponse.data.length, 'employees');
        
        // Also refresh online users when refreshing employee data
        await fetchOnlineUsers();
      }

    } catch (error) {
      console.error('Error refreshing dashboard data:', error);
      toast({
        title: "Refresh Error",
        description: "Failed to refresh dashboard data",
        variant: "destructive",
      });
    }
  };

  // Fetch online users for presence indicators
  const fetchOnlineUsers = async () => {
    try {
      const response = await presenceAPI.getOnlineUsers();
      const onlineUserIds = new Set(response.data.online_users.map(user => user.id));
      setOnlineUsers(onlineUserIds);
      console.log('Online users updated:', onlineUserIds.size, 'users online');
    } catch (error) {
      console.error('Error fetching online users:', error);
      // Don't show toast for presence errors as they're not critical
    }
  };

  // Initialize default chat rooms for all users
  const initializeDefaultRooms = async () => {
    try {
      const response = await chatAPI.createDefaultRooms();
      console.log('Default rooms created:', response.data);
      toast({
        title: "Chat Setup Complete",
        description: `Created ${response.data.rooms_created} default chat rooms`,
      });
    } catch (error) {
      console.error('Failed to create default rooms:', error);
      // Don't show error toast as it's not critical for users
    }
  };

  useEffect(() => {
    if (user && (isAdmin() || isEmployee())) {
      // Only admin can initialize default rooms, do it once
      const hasInitialized = localStorage.getItem('chat_rooms_initialized');
      if (!hasInitialized && isAdmin()) {
        initializeDefaultRooms();
        localStorage.setItem('chat_rooms_initialized', 'true');
      }
    }
  }, [user, isAdmin, isEmployee]);

  const handleShowChat = () => {
    setShowChat(true);
  };

  const handleAssignChatbot = async (chatbotId, employeeId) => {
    try {
      const response = await dashboardAPI.assignChatbot({
        chatbot_id: chatbotId,
        employee_id: employeeId,
        notes: "Assigned via admin dashboard"
      });
      
      toast({
        title: "Assignment successful!",
        description: "The chatbot has been assigned to the employee",
      });
      
      // Refresh data immediately
      await refreshDashboardData();
      
    } catch (error) {
      const errorMessage = handleAPIError(error, 'Failed to assign chatbot');
      toast({
        title: "Assignment failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Pagination logic
  const getPaginatedChatbots = () => {
    if (showAllChatbots) {
      const indexOfLastChatbot = currentPage * chatbotsPerPage;
      const indexOfFirstChatbot = indexOfLastChatbot - chatbotsPerPage;
      return chatbots.slice(indexOfFirstChatbot, indexOfLastChatbot);
    }
    return chatbots.slice(0, 5); // Show only 5 in compact mode
  };

  const totalPages = Math.ceil(chatbots.length / chatbotsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleShowAllChatbots = () => {
    setShowAllChatbots(!showAllChatbots);
    setCurrentPage(1); // Reset to first page when toggling
  };

  // User management functions
  const loadAllUsersData = async () => {
    try {
      const response = await dashboardAPI.getAllUsersData();
      setAllUsersData(response.data.users || []);
    } catch (error) {
      console.error('Failed to load all users data:', error);
      toast({
        title: "Error",
        description: "Failed to load user data",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`Are you sure you want to delete user ${userEmail}? This action cannot be undone.`)) {
      try {
        await dashboardAPI.deleteUserByAdmin(userId);
        toast({
          title: "User Deleted",
          description: `User ${userEmail} has been deleted successfully`,
        });
        // Refresh user data
        await loadAllUsersData();
      } catch (error) {
        console.error('Failed to delete user:', error);
        toast({
          title: "Error",
          description: "Failed to delete user",
          variant: "destructive",
        });
      }
    }
  };

  // Customer/Demo Dashboard - Show chatbot creation or existing chatbots
  if (isCustomer() || isDemo()) {
    const hasChatbots = chatbots && chatbots.length > 0;
    const hasMaxChatbots = chatbots && chatbots.length >= 1; // One chatbot limit
    console.log('Customer/Demo dashboard - hasChatbots:', hasChatbots, 'hasMaxChatbots:', hasMaxChatbots, 'chatbots:', chatbots);

    return (
      <div className="pt-16 min-h-screen bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950">
        <SEOHead 
          title="Dashboard - BrixBot" 
          description="Manage your AI chatbots and customer service automation."
          isIndexable={false}
        />
        <div className="max-w-6xl mx-auto p-6">
          {/* Enhanced Header */}
          <div className="mb-10">
            <div className="flex flex-col space-y-6 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-violet-600/5 to-purple-600/5 rounded-2xl blur-xl"></div>
                <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-2xl p-8 border border-slate-800/50 shadow-2xl transition-all duration-300 hover:scale-[1.01] hover:shadow-xl hover:border-violet-500/20">
                  <h1 className="text-4xl font-bold text-white mb-2">
                    Welcome back, {(() => {
                      const firstName = user?.first_name?.trim();
                      const lastName = user?.last_name?.trim();
                      
                      if (firstName && lastName) {
                        return `${firstName} ${lastName}`;
                      }
                      if (firstName) {
                        return firstName;
                      }
                      if (lastName) {
                        return lastName;
                      }
                      
                      const email = user?.email;
                      if (email) {
                        const emailParts = email.split('@')[0];
                        const nameFromEmail = emailParts
                          .replace(/[._]/g, ' ')
                          .split(' ')
                          .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                          .join(' ');
                        
                        if (nameFromEmail && !/^\d+$/.test(nameFromEmail)) {
                          return nameFromEmail;
                        }
                      }
                      
                      return email;
                    })()}
                  </h1>
                  <p className="text-gray-300 text-lg">
                    {isDemo() ? 'Demo Account' : 'Customer Dashboard'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-violet-600/20 to-purple-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                  <Button
                    onClick={handleShowChat}
                    className="relative bg-slate-800/90 hover:bg-slate-700/90 border border-slate-700/50 text-white px-6 py-3 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] hover:border-violet-500/30"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Support
                    {unreadMessageCount > 0 && (
                      <Badge className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                        {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                      </Badge>
                    )}
                  </Button>
                </div>
                {isDemo() && (
                  <div className="relative group">
                    <Badge className="bg-violet-500/20 text-violet-200 border border-violet-400/30 backdrop-blur-xl px-4 py-2 transition-all duration-300 hover:bg-violet-500/30">
                      <Crown className="w-4 h-4 mr-2" />
                      Demo Mode
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!hasChatbots ? (
            // Clean no chatbots section
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="relative max-w-xl w-full">
                <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/5 via-purple-600/5 to-violet-500/5 rounded-3xl blur-2xl"></div>
                
                <Card className="relative bg-slate-900/90 backdrop-blur-2xl border border-slate-800/50 shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-violet-500/10 hover:border-violet-500/20">
                  <CardContent className="p-12 text-center">
                    <div className="mb-8">
                      <div className="relative w-24 h-24 mx-auto mb-8">
                        <div className="w-24 h-24 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg shadow-violet-500/25">
                          <Plus className="w-12 h-12 text-white" />
                        </div>
                      </div>
                      <h2 className="text-3xl font-bold text-white mb-4">
                        Create Your AI Chatbot
                      </h2>
                      <p className="text-gray-300 text-lg leading-relaxed">
                        Transform your customer service with an intelligent AI assistant.
                      </p>
                    </div>

                    <div className="relative group mb-8">
                      <div className="absolute -inset-1 bg-gradient-to-r from-violet-600/30 to-purple-500/30 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                      <Button
                        onClick={() => setShowChatbotCreator(true)}
                        className="relative bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-8 py-4 text-lg font-medium rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-violet-500/25"
                      >
                        <Sparkles className="w-6 h-6 mr-3" />
                        Get Started
                      </Button>
                    </div>

                    <div className="flex justify-center space-x-4">
                      <Button
                        onClick={handleShowChat}
                        variant="outline"
                        className="bg-slate-800/50 border-slate-700/50 text-gray-300 hover:bg-slate-700/50 hover:text-white hover:border-violet-500/30 px-6 py-2 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Need Help?
                      </Button>
                      <Button
                        onClick={() => refreshDashboardData()}
                        variant="outline"
                        className="bg-slate-800/50 border-slate-700/50 text-gray-300 hover:bg-slate-700/50 hover:text-white hover:border-violet-500/30 px-6 py-2 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                    </div>

                    {isDemo() && (
                      <div className="mt-8">
                        <div className="p-4 bg-violet-900/10 border border-violet-500/20 rounded-lg backdrop-blur-xl">
                          <p className="text-violet-200 text-sm flex items-center justify-center">
                            <Crown className="w-4 h-4 mr-2" />
                            Demo mode - No payment required
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : hasMaxChatbots ? (
            // Enhanced dashboard with subtle animations
            <div className="space-y-8">
              {/* Phase Roadmap with subtle violet undertones */}
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-violet-600/5 to-purple-600/5 rounded-2xl blur-xl"></div>
                <div className="relative bg-slate-900/90 backdrop-blur-2xl rounded-2xl border border-slate-800/50 shadow-2xl transition-all duration-300 hover:scale-[1.005] hover:border-violet-500/20">
                  <ChatbotPhaseRoadmap chatbot={chatbots[0]} className="border-0 shadow-none bg-transparent" />
                </div>
              </div>
              
              {/* Premium Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    title: "Your Chatbot",
                    value: stats.total_chatbots || 1,
                    subtitle: "Active",
                    icon: Bot,
                    gradient: "from-violet-500 to-purple-500",
                    hoverGlow: "hover:shadow-violet-500/20"
                  },
                  {
                    title: "Current Phase", 
                    value: chatbots[0]?.status?.replace('_', ' ') || 'Pending',
                    subtitle: "In Progress",
                    icon: Clock,
                    gradient: "from-blue-500 to-indigo-500",
                    hoverGlow: "hover:shadow-blue-500/20"
                  },
                  {
                    title: "Progress",
                    value: `${(() => {
                      const phases = ['pending', 'in_progress', 'testing', 'final_payment', 'deploy', 'done'];
                      const currentIndex = phases.indexOf(chatbots[0]?.status) + 1;
                      return Math.round((currentIndex / phases.length) * 100);
                    })()}%`,
                    subtitle: "Complete",
                    icon: CheckCircle,
                    gradient: "from-green-500 to-emerald-500",
                    hoverGlow: "hover:shadow-green-500/20"
                  }
                ].map((stat, index) => (
                  <div key={stat.title} className="relative group">
                    <Card className={`bg-slate-800/80 backdrop-blur-2xl border border-slate-700/50 shadow-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:-translate-y-1 ${stat.hoverGlow} hover:border-violet-500/20 group`}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-gray-400 text-sm font-medium">{stat.title}</p>
                            <p className="text-2xl font-bold text-white transition-all duration-300">
                              {stat.value}
                            </p>
                            <p className={`text-xs font-medium bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                              {stat.subtitle}
                            </p>
                          </div>
                          <div className={`w-14 h-14 bg-gradient-to-r ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110`}>
                            <stat.icon className="w-7 h-7 text-white" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>

              {/* Enhanced Chatbot Details Card with Floating Effects */}
              <div className="relative animate-slideInRight">
                <div className="absolute -inset-1 bg-gradient-to-r from-violet-600/20 to-blue-600/20 rounded-2xl blur-xl"></div>
                <Card className="relative bg-slate-800/90 backdrop-blur-2xl border border-slate-700/50 shadow-2xl hover:shadow-violet-500/20 transition-all duration-500 hover:scale-[1.01] hover:-translate-y-1">
                  <CardHeader className="border-b border-slate-700/50 pb-6">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-slate-100 flex items-center text-xl font-semibold">
                        <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-blue-500 rounded-lg flex items-center justify-center mr-3 animate-float">
                          <Building className="w-4 h-4 text-white" />
                        </div>
                        Your Chatbot
                      </CardTitle>
                      <Badge className={`${getStatusColor(chatbots[0]?.status)} px-4 py-2 text-sm font-medium animate-pulse`}>
                        {chatbots[0]?.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="space-y-8">
                      <div className="flex items-center space-x-6">
                        <div className="relative group">
                          <div className="absolute -inset-2 bg-gradient-to-r from-violet-600 to-blue-500 rounded-2xl blur opacity-40 group-hover:opacity-60 transition duration-300"></div>
                          <div className="relative w-20 h-20 bg-gradient-to-r from-violet-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-all duration-300">
                            <Bot className="w-10 h-10 text-white" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold bg-gradient-to-r from-slate-100 to-slate-300 bg-clip-text text-transparent">
                            {chatbots[0]?.company_name}
                          </h3>
                          <p className="text-slate-300 text-lg">{chatbots[0]?.company_website}</p>
                          <p className="text-slate-400 text-sm">Created {formatDate(chatbots[0]?.created_at)}</p>
                        </div>
                      </div>
                      
                      {/* Enhanced Payment Status */}
                      <div className="relative">
                        <div className="absolute -inset-1 bg-gradient-to-r from-slate-700/50 to-slate-600/50 rounded-xl blur"></div>
                        <div className="relative flex items-center justify-between p-6 bg-slate-700/30 backdrop-blur-xl rounded-xl border border-slate-600/30">
                          <div>
                            <p className="text-slate-300 text-sm font-medium mb-2">Payment Status</p>
                            <div className="flex items-center space-x-3">
                              {chatbots[0]?.deposit_paid ? (
                                <>
                                  <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center animate-pulse">
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="text-green-400 font-semibold text-lg">Deposit Paid</span>
                                </>
                              ) : (
                                <>
                                  <div className="w-6 h-6 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center animate-pulse">
                                    <Clock className="w-4 h-4 text-white" />
                                  </div>
                                  <span className="text-amber-400 font-semibold text-lg">Deposit Required</span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Enhanced Phase-specific action buttons */}
                          <div className="flex space-x-3">
                            {!chatbots[0]?.deposit_paid && (
                              <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-violet-600 to-purple-500 rounded-lg blur opacity-50 group-hover:opacity-80 transition duration-300"></div>
                                <Button
                                  onClick={() => handleShowPaymentModal(chatbots[0])}
                                  className="relative bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white px-6 py-3 shadow-xl hover:scale-105 transition-all duration-300"
                                >
                                  <DollarSign className="w-4 h-4 mr-2" />
                                  Pay Deposit
                                </Button>
                              </div>
                            )}
                            
                            {chatbots[0]?.status === 'testing' && (
                              <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-lg blur opacity-50 group-hover:opacity-80 transition duration-300"></div>
                                <Button
                                  onClick={() => {
                                    setSelectedChatbot(chatbots[0]);
                                    setShowCustomerTesting(true);
                                  }}
                                  className="relative bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-6 py-3 shadow-xl hover:scale-105 transition-all duration-300"
                                >
                                  <TestTube className="w-4 h-4 mr-2" />
                                  Test Chatbot
                                </Button>
                              </div>
                            )}
                            
                            {chatbots[0]?.status === 'final_payment' && (
                              <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-amber-600 to-orange-500 rounded-lg blur opacity-50 group-hover:opacity-80 transition duration-300"></div>
                                <Button
                                  onClick={() => {
                                    setSelectedChatbot(chatbots[0]);
                                    setShowPlanSelection(true);
                                  }}
                                  className="relative bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white px-6 py-3 shadow-xl hover:scale-105 transition-all duration-300"
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Complete Payment
                                </Button>
                              </div>
                            )}
                            
                            {chatbots[0]?.status === 'deploy' && (
                              <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-green-600 to-emerald-500 rounded-lg blur opacity-50 group-hover:opacity-80 transition duration-300"></div>
                                <Button
                                  onClick={() => {
                                    setSelectedChatbot(chatbots[0]);
                                    setShowDeployment(true);
                                  }}
                                  className="relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-6 py-3 shadow-xl hover:scale-105 transition-all duration-300"
                                >
                                  <Rocket className="w-4 h-4 mr-2" />
                                  Deploy Now
                                </Button>
                              </div>
                            )}
                            
                            {chatbots[0]?.status === 'done' && (
                              <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-slate-600 to-slate-500 rounded-lg blur opacity-50 group-hover:opacity-80 transition duration-300"></div>
                                <Button
                                  onClick={() => {
                                    setSelectedChatbot(chatbots[0]);
                                    setShowDeployment(true);
                                  }}
                                  className="relative bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 text-white px-6 py-3 shadow-xl hover:scale-105 transition-all duration-300"
                                >
                                  <Monitor className="w-4 h-4 mr-2" />
                                  View Instructions
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Enhanced View Details Button */}
                      <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-slate-600/50 to-slate-500/50 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                        <Button
                          onClick={() => {
                            setSelectedChatbot(chatbots[0]);
                            setShowChatbotDetails(true);
                          }}
                          variant="outline"
                          className="relative w-full bg-slate-700/30 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:text-white backdrop-blur-xl py-4 text-lg transition-all duration-300 hover:scale-[1.02]"
                        >
                          <Eye className="w-5 h-5 mr-3" />
                          View Full Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Enhanced Quick Actions Card */}
              <div className="relative animate-slideInLeft">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-cyan-500/20 rounded-2xl blur-xl"></div>
                <Card className="relative bg-slate-800/90 backdrop-blur-2xl border border-slate-700/50 shadow-2xl hover:shadow-blue-500/20 transition-all duration-500 hover:scale-[1.01] hover:-translate-y-1">
                  <CardHeader className="border-b border-slate-700/50 pb-6">
                    <CardTitle className="text-slate-100 flex items-center text-xl font-semibold">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3 animate-float">
                        <Zap className="w-4 h-4 text-white" />
                      </div>
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8 space-y-4">
                    <div className="relative group">
                      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/50 to-cyan-500/50 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                      <Button
                        onClick={handleShowChat}
                        className="relative w-full bg-slate-700/50 hover:bg-slate-600/70 border border-slate-600/50 text-slate-200 hover:text-white justify-start py-4 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                      >
                        <MessageCircle className="w-5 h-5 mr-4" />
                        Contact Support
                        {unreadMessageCount > 0 && (
                          <Badge className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs ml-auto animate-bounce shadow-lg">
                            {unreadMessageCount}
                          </Badge>
                        )}
                      </Button>
                    </div>
                    
                    <div className="relative group">
                      <div className="absolute -inset-1 bg-gradient-to-r from-slate-600/50 to-slate-500/50 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                      <Button
                        onClick={refreshDashboardData}
                        variant="outline"
                        className="relative w-full bg-slate-700/30 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:text-white justify-start py-4 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                      >
                        <RefreshCw className="w-5 h-5 mr-4" />
                        Refresh Dashboard
                      </Button>
                    </div>
                    
                    {chatbots[0]?.status !== 'done' && (
                      <div className="relative">
                        <div className="absolute -inset-1 bg-gradient-to-r from-violet-600/30 to-purple-500/30 rounded-lg blur"></div>
                        <div className="relative p-4 bg-violet-900/20 rounded-lg border border-violet-500/30 backdrop-blur-xl">
                          <p className="text-violet-200 text-sm flex items-center">
                            <Sparkles className="w-4 h-4 mr-3 animate-spin-slow" />
                            One chatbot limit: You can create another chatbot after this one is completed.
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            // Fallback - Should not reach here with one chatbot limit
            <div className="text-center py-12">
              <p className="text-gray-400">No chatbots found</p>
            </div>
          )}
        </div>

        {/* Chatbot Creator Modal */}
        {showChatbotCreator && (
          <ChatbotCreator
            onClose={() => setShowChatbotCreator(false)}
            onSuccess={handleChatbotCreated}
            existingChatbots={chatbots}
          />
        )}

        {/* Chat Modal */}
        <SimpleChatSystem
          isOpen={showChat}
          onClose={() => setShowChat(false)}
        />

        {/* Chatbot Details Modal */}
        {showChatbotDetails && selectedChatbot && (
          <ChatbotDetailsModal
            chatbot={selectedChatbot}
            isOpen={showChatbotDetails}
            onClose={() => {
              setShowChatbotDetails(false);
              setSelectedChatbot(null);
            }}
            onUpdate={refreshDashboardData}
            onDelete={() => {
              refreshDashboardData();
              setSelectedChatbot(null);
            }}
          />
        )}

        {/* Payment Modal */}
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentChatbot(null);
          }}
          chatbot={paymentChatbot}
          onPaymentSuccess={handlePaymentSuccess}
        />

        {/* Customer Testing Modal */}
        <CustomerTestingModal
          isOpen={showCustomerTesting}
          onClose={() => {
            setShowCustomerTesting(false);
            setSelectedChatbot(null);
          }}
          chatbot={selectedChatbot}
          onUpdate={refreshDashboardData}
        />

        {/* Plan Selection Modal */}
        <PlanSelectionModal
          isOpen={showPlanSelection}
          onClose={() => {
            setShowPlanSelection(false);
            setSelectedChatbot(null);
          }}
          chatbot={selectedChatbot}
          onUpdate={refreshDashboardData}
        />

        {/* Deployment Modal */}
        <DeploymentModal
          isOpen={showDeployment}
          onClose={() => {
            setShowDeployment(false);
            setSelectedChatbot(null);
          }}
          chatbot={selectedChatbot}
          onUpdate={refreshDashboardData}
        />
      </div>
    );
  }

  // Employee Dashboard
  if (isEmployee()) {
    const assignedCount = chatbots.length;
    const inProgressCount = chatbots.filter(cb => cb.status === 'in_progress').length;
    const testingCount = chatbots.filter(cb => cb.status === 'testing').length;
    const deployCount = chatbots.filter(cb => cb.status === 'deploy').length;
    const doneCount = chatbots.filter(cb => cb.status === 'done' || cb.status === 'completed' || cb.status === 'deployed').length;

    return (
      <div className="pt-16 min-h-screen bg-gradient-to-br from-gray-950 via-slate-900 to-gray-950">
        <div className="max-w-7xl mx-auto p-6">
          {/* Enhanced Header */}
          <div className="mb-8">
            <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-600/5 to-indigo-600/5 rounded-2xl blur-xl"></div>
                <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-2xl p-8 border border-slate-800/50 shadow-2xl transition-all duration-300 hover:scale-[1.01] hover:shadow-xl hover:border-blue-500/20">
                  <h1 className="text-4xl font-bold text-white mb-2">Employee Dashboard</h1>
                  <p className="text-gray-300 text-lg">View and manage your assigned chatbots</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 to-indigo-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                  <Button
                    onClick={handleShowChat}
                    className="relative bg-slate-800/90 hover:bg-slate-700/90 border border-slate-700/50 text-white px-6 py-3 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] hover:border-blue-500/30"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Team Chat
                    {unreadMessageCount > 0 && (
                      <Badge className="bg-red-500 text-white text-xs px-2 py-1 rounded-full ml-2">
                        {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                      </Badge>
                    )}
                  </Button>
                </div>
                <div className="relative group">
                  <Button
                    onClick={refreshDashboardData}
                    variant="outline"
                    className="bg-slate-800/50 border-slate-700/50 text-gray-300 hover:bg-slate-700/50 hover:text-white hover:border-indigo-500/30 backdrop-blur-xl transition-all duration-300 hover:scale-[1.02]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Premium Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {[
              {
                title: "Assigned",
                value: assignedCount,
                icon: UserCheck,
                gradient: "from-violet-500 to-purple-500",
                hoverGlow: "hover:shadow-violet-500/20"
              },
              {
                title: "In Progress", 
                value: inProgressCount,
                icon: Clock,
                gradient: "from-amber-500 to-orange-500",
                hoverGlow: "hover:shadow-amber-500/20"
              },
              {
                title: "Testing",
                value: testingCount,
                icon: TestTube,
                gradient: "from-blue-500 to-indigo-500",
                hoverGlow: "hover:shadow-blue-500/20"
              },
              {
                title: "Deploy",
                value: deployCount,
                icon: Rocket,
                gradient: "from-green-500 to-emerald-500",
                hoverGlow: "hover:shadow-green-500/20"
              },
              {
                title: "Completed",
                value: doneCount,
                icon: CheckCircle,
                gradient: "from-indigo-500 to-purple-500",
                hoverGlow: "hover:shadow-indigo-500/20"
              }
            ].map((stat, index) => (
              <div key={stat.title} className="relative group">
                <Card className={`bg-slate-800/80 backdrop-blur-2xl border border-slate-700/50 shadow-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:-translate-y-1 ${stat.hoverGlow} hover:border-violet-500/20 group`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm font-medium">{stat.title}</p>
                        <p className="text-2xl font-bold text-white transition-all duration-300">
                          {stat.value}
                        </p>
                      </div>
                      <div className={`w-14 h-14 bg-gradient-to-r ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110`}>
                        <stat.icon className="w-7 h-7 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Chatbots List */}
          <Card className="bg-gray-900/50 border-violet-500/20">
            <CardHeader className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div>
                <CardTitle className="text-white">My Assigned Chatbots</CardTitle>
                <CardDescription className="text-gray-400">Chatbots assigned to you</CardDescription>
              </div>
              <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-3 sm:space-y-0">
                <Button onClick={refreshDashboardData} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700 w-full sm:w-auto">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {chatbots.length > 0 ? (
                  chatbots.map((chatbot) => (
                    <div key={chatbot.id} className="p-4 border border-gray-700 rounded-lg hover:border-violet-500/50 transition-colors">
                      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                        <div className="flex items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-white font-semibold text-sm sm:text-base truncate">{chatbot.company_name}</h3>
                            <p className="text-gray-400 text-xs sm:text-sm truncate">{chatbot.company_website}</p>
                            <p className="text-gray-500 text-xs">Created {formatDate(chatbot.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3 flex-shrink-0">
                          <Badge className={`${getStatusColor(chatbot.status)} border-current text-xs text-center justify-center`}>
                            {chatbot.status.replace('_', ' ')}
                          </Badge>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedChatbot(chatbot);
                                setShowChatbotDetails(true);
                              }}
                              className="text-violet-400 hover:text-violet-300 text-xs sm:text-sm w-full sm:w-auto"
                            >
                              <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No assigned chatbots yet</p>
                    <p className="text-gray-500 text-sm">Assignments will appear here</p>
                    <Button onClick={refreshDashboardData} className="mt-4 bg-violet-600 hover:bg-violet-700">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh Data
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modals */}
        <SimpleChatSystem isOpen={showChat} onClose={() => setShowChat(false)} />
        {showChatbotDetails && selectedChatbot && (
          <ChatbotDetailsModal
            chatbot={selectedChatbot}
            isOpen={showChatbotDetails}
            onClose={() => {
              setShowChatbotDetails(false);
              setSelectedChatbot(null);
            }}
            onUpdate={refreshDashboardData}
            onDelete={() => {
              refreshDashboardData();
              setSelectedChatbot(null);
            }}
          />
        )}
      </div>
    );
  }

  // Admin Dashboard
  if (isAdmin()) {
    return (
      <div className="pt-16 min-h-screen bg-gray-950">
        <div className="max-w-7xl mx-auto p-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-gray-400 text-sm sm:text-base">
                  Manage users, chatbots, and monitor system performance
                </p>
              </div>
              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
                <Button
                  onClick={() => setShowEmployeeManagement(true)}
                  className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                >
                  <Users className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Manage </span>Employees
                </Button>
                <Button
                  onClick={handleShowChat}
                  className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto relative"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat
                  {unreadMessageCount > 0 && (
                    <Badge className="bg-red-600 text-white text-xs px-2 py-1 rounded-full ml-2 min-w-[20px] h-[20px] flex items-center justify-center">
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </Badge>
                  )}
                </Button>
                <Badge className="bg-red-600/20 text-red-300 border-red-500/30 justify-center sm:justify-start">
                  <Shield className="w-4 h-4 mr-2" />
                  Administrator
                </Badge>
              </div>
            </div>
          </div>

          {/* Admin Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total Users</p>
                    <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total Chatbots</p>
                    <p className="text-2xl font-bold text-white">{stats.total_chatbots || 0}</p>
                  </div>
                  <Bot className="w-8 h-8 text-violet-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Total Sales</p>
                    <p className="text-2xl font-bold text-white">{stats.total_sales || 0}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Active Chats</p>
                    <p className="text-2xl font-bold text-white">{stats.active_conversations || 0}</p>
                  </div>
                  <MessageCircle className="w-8 h-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
            {/* Chatbots List */}
            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardHeader>
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                  <div>
                    <CardTitle className="text-white">
                      {showAllChatbots ? 'All Chatbots' : 'Recent Chatbots'}
                    </CardTitle>
                    <CardDescription className="text-gray-400 text-sm">
                      {showAllChatbots 
                        ? `Showing ${chatbots.length} total chatbots with pagination`
                        : 'Latest 5 chatbot requests and their status'
                      }
                    </CardDescription>
                  </div>
                  <Button
                    onClick={toggleShowAllChatbots}
                    variant="outline"
                    size="sm"
                    className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10 w-full sm:w-auto"
                  >
                    {showAllChatbots ? 'Show Recent Only' : 'Show All Chatbots'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {chatbots.length > 0 ? (
                    <>
                      <div className="space-y-4">
                        {getPaginatedChatbots().map((chatbot) => (
                        <div
                          key={chatbot.id}
                          className="p-4 border border-gray-700 rounded-lg hover:border-violet-500/50 transition-colors"
                        >
                          <div className="flex flex-col space-y-3">
                            {/* First row: Company info */}
                            <div className="flex items-center space-x-3 sm:space-x-4">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Building className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center space-x-3">
                                  <h3 className="text-white font-semibold text-sm sm:text-base truncate">{chatbot.company_name}</h3>
                                  {chatbot.plan_type && (
                                    <Badge className="bg-green-600 text-white text-xs">
                                      {(() => {
                                        // Try to get human-readable plan name first
                                        const planId = chatbot.selected_plan_id || chatbot.selected_price_id;
                                        const planName = planId ? planIdToName[planId] : null;
                                        
                                        if (planName) {
                                          return planName;
                                        }
                                        
                                        // Fallback to plan type + billing cycle
                                        const typeCapitalized = chatbot.plan_type === 'starter' ? 'Starter' :
                                                               chatbot.plan_type === 'professional' ? 'Professional' :
                                                               chatbot.plan_type === 'enterprise' ? 'Enterprise' : 
                                                               chatbot.plan_type.charAt(0).toUpperCase() + chatbot.plan_type.slice(1);
                                        const cycle = chatbot.billing_cycle ? ` ${chatbot.billing_cycle.charAt(0).toUpperCase() + chatbot.billing_cycle.slice(1)}` : '';
                                        return `${typeCapitalized}${cycle}`;
                                      })()}
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-gray-400 text-xs sm:text-sm truncate">{chatbot.company_website}</p>
                                <p className="text-gray-500 text-xs">Created {formatDate(chatbot.created_at)}</p>
                                {chatbot.assigned_employee_id && (
                                  <p className="text-blue-400 text-xs truncate">
                                    Assigned to: {employees.find(e => e.id === chatbot.assigned_employee_id)?.first_name || 'Employee'} {employees.find(e => e.id === chatbot.assigned_employee_id)?.last_name || ''}
                                  </p>
                                )}
                              </div>
                            </div>
                            
                            {/* Second row: Status and actions */}
                            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                              <div className="flex items-center space-x-2">
                                <Badge className={`${getStatusColor(chatbot.status)} border-current text-xs`}>
                                  {chatbot.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              
                              <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
                                {/* Assignment dropdown */}
                                {(chatbot.status === 'pending' || !chatbot.assigned_employee_id) && employees.length > 0 && (
                                  <select
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        handleAssignChatbot(chatbot.id, e.target.value);
                                        e.target.value = ''; // Reset dropdown
                                      }
                                    }}
                                    className="bg-gray-800 border border-gray-600 text-white text-xs rounded px-2 py-1 w-full sm:w-auto sm:min-w-[120px]"
                                    defaultValue=""
                                  >
                                    <option value="" disabled>
                                      {chatbot.assigned_employee_id ? 'Reassign to...' : 'Assign to...'}
                                    </option>
                                    {employees.map((employee) => (
                                      <option key={employee.id} value={employee.id}>
                                        {employee.first_name} {employee.last_name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                                
                                {/* View Details button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedChatbot(chatbot);
                                    setShowChatbotDetails(true);
                                  }}
                                  className="text-violet-400 hover:text-violet-300 text-xs w-full sm:w-auto"
                                >
                                  <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                                  View Details
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      </div>
                      
                      {/* Pagination Controls */}
                      {showAllChatbots && totalPages > 1 && (
                        <div className="flex flex-col space-y-3 pt-4 border-t border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                          <div className="text-sm text-gray-400 text-center sm:text-left">
                            Showing {((currentPage - 1) * chatbotsPerPage) + 1} to {Math.min(currentPage * chatbotsPerPage, chatbots.length)} of {chatbots.length} chatbots
                          </div>
                          <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                            <Button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              variant="outline"
                              size="sm"
                              className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 px-2 sm:px-3"
                            >
                              <span className="hidden sm:inline">Previous</span>
                              <span className="sm:hidden">‹</span>
                            </Button>
                            
                            {/* Show fewer page numbers on mobile */}
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              const page = i + 1;
                              const showPage = totalPages <= 5 || 
                                             page === 1 || 
                                             page === totalPages || 
                                             Math.abs(page - currentPage) <= 1;
                              
                              if (!showPage) return null;
                              
                              return (
                                <Button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  className={
                                    currentPage === page
                                      ? "bg-violet-600 hover:bg-violet-700 text-white px-2 sm:px-3"
                                      : "border-gray-600 text-gray-300 hover:bg-gray-700 px-2 sm:px-3"
                                  }
                                >
                                  {page}
                                </Button>
                              );
                            })}
                            
                            <Button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              variant="outline"
                              size="sm"
                              className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 px-2 sm:px-3"
                            >
                              <span className="hidden sm:inline">Next</span>
                              <span className="sm:hidden">›</span>
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Bot className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No chatbots found</p>
                      <p className="text-gray-500 text-sm">Chatbot requests will appear here</p>
                      <Button
                        onClick={refreshDashboardData}
                        className="mt-4 bg-violet-600 hover:bg-violet-700"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh Data
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardHeader>
                <CardTitle className="text-white">Recent Activity</CardTitle>
                <CardDescription className="text-gray-400">
                  Latest system activities and updates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivity.slice(0, 5).map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <Activity className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm">{activity.message}</p>
                        <p className="text-gray-400 text-xs">{formatDate(activity.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                  {recentActivity.length === 0 && (
                    <p className="text-gray-400 text-center py-8">No recent activity</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Management Section */}
          <div className="mt-8">
            <Card className="bg-gray-900/50 border-violet-500/20">
              <CardHeader>
                <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                  <div>
                    <CardTitle className="text-white">User Management</CardTitle>
                    <CardDescription className="text-gray-400 text-sm">
                      Manage all registered users and their account data
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowUserManagement(!showUserManagement)}
                    variant="outline"
                    size="sm"
                    className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10 w-full sm:w-auto"
                  >
                    {showUserManagement ? 'Hide User Data' : 'Show All Users'}
                  </Button>
                </div>
              </CardHeader>
              {showUserManagement && (
                <CardContent>
                  <div className="space-y-4">
                    {allUsersData.length > 0 ? (
                      allUsersData.map((userData) => (
                        <div
                          key={userData.id}
                          className="p-4 border border-gray-700 rounded-lg hover:border-violet-500/50 transition-colors"
                        >
                          <div className="flex flex-col space-y-3">
                            {/* User Info Row */}
                            <div className="flex items-center space-x-3 sm:space-x-4">
                              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                userData.role === 'admin' ? 'bg-red-600' :
                                userData.role === 'employee' ? 'bg-blue-600' :
                                userData.role === 'demo' ? 'bg-purple-600' : 'bg-green-600'
                              }`}>
                                {userData.role === 'admin' ? <Crown className="w-5 h-5 text-white" /> :
                                 userData.role === 'employee' ? <Briefcase className="w-5 h-5 text-white" /> :
                                 userData.role === 'demo' ? <Star className="w-5 h-5 text-white" /> :
                                 <Users className="w-5 h-5 text-white" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                                  <div className="min-w-0">
                                    <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                                      {userData.first_name && userData.last_name 
                                        ? `${userData.first_name} ${userData.last_name}` 
                                        : userData.email.split('@')[0]}
                                    </h3>
                                    <p className="text-gray-400 text-xs sm:text-sm truncate">{userData.email}</p>
                                    {userData.phone_number && (
                                      <p className="text-gray-500 text-xs truncate">{userData.phone_number}</p>
                                    )}
                                    {userData.company_name && (
                                      <p className="text-gray-500 text-xs truncate">{userData.company_name}</p>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2 mt-2 sm:mt-0">
                                    <Badge className={`text-xs ${
                                      userData.role === 'admin' ? 'bg-red-600/20 text-red-300 border-red-500/30' :
                                      userData.role === 'employee' ? 'bg-blue-600/20 text-blue-300 border-blue-500/30' :
                                      userData.role === 'demo' ? 'bg-purple-600/20 text-purple-300 border-purple-500/30' :
                                      'bg-green-600/20 text-green-300 border-green-500/30'
                                    }`}>
                                      {userData.role}
                                    </Badge>
                                    {userData.is_verified ? (
                                      <Badge className="bg-green-600/20 text-green-300 border-green-500/30 text-xs">Verified</Badge>
                                    ) : (
                                      <Badge className="bg-yellow-600/20 text-yellow-300 border-yellow-500/30 text-xs">Unverified</Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Chatbots Row - Only for customers with chatbots */}
                            {userData.role === 'customer' && userData.chatbots && userData.chatbots.length > 0 && (
                              <div className="pt-3 border-t border-gray-700">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Bot className="w-4 h-4 text-violet-400" />
                                  <span className="text-gray-400 text-sm font-medium">Chatbots ({userData.chatbots.length})</span>
                                </div>
                                <div className="space-y-2">
                                  {userData.chatbots.map((chatbot) => (
                                    <div key={chatbot.id} className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg">
                                      <div className="flex items-center space-x-2">
                                        <div className="w-2 h-2 rounded-full bg-violet-400"></div>
                                        <span className="text-white text-sm font-medium">{chatbot.company_name}</span>
                                        <Badge className={`text-xs ${getStatusColor(chatbot.status)}`}>
                                          {chatbot.status}
                                        </Badge>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        {chatbot.company_website && (
                                          <Globe className="w-3 h-3 text-gray-500" />
                                        )}
                                        <span className="text-gray-500 text-xs">
                                          {formatDate(chatbot.created_at)}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Actions Row */}
                            <div className="flex items-center justify-between">
                              <div className="text-gray-400 text-xs">
                                Created: {formatDate(userData.created_at)}
                                {userData.role === 'customer' && userData.total_chatbots > 0 && (
                                  <span className="ml-2 text-violet-400">• {userData.total_chatbots} chatbot{userData.total_chatbots !== 1 ? 's' : ''}</span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(userData.id, userData.email)}
                                  className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No users found</p>
                        <p className="text-gray-500 text-sm">Try refreshing the data</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        </div>

        {/* Modals reused here as needed for admin too */}
        <SimpleChatSystem
          isOpen={showChat}
          onClose={() => setShowChat(false)}
        />

        {showChatbotDetails && selectedChatbot && (
          <ChatbotDetailsModal
            chatbot={selectedChatbot}
            isOpen={showChatbotDetails}
            onClose={() => {
              setShowChatbotDetails(false);
              setSelectedChatbot(null);
            }}
            onUpdate={refreshDashboardData}
            onDelete={() => {
              refreshDashboardData();
              setSelectedChatbot(null);
            }}
          />
        )}

        <DeploymentModal
          isOpen={showDeployment}
          onClose={() => {
            setShowDeployment(false);
            setSelectedChatbot(null);
          }}
          chatbot={selectedChatbot}
          onUpdate={refreshDashboardData}
        />

        <PlanSelectionModal
          isOpen={showPlanSelection}
          onClose={() => {
            setShowPlanSelection(false);
            setSelectedChatbot(null);
          }}
          chatbot={selectedChatbot}
          onUpdate={refreshDashboardData}
        />

        <EmployeeManagementModal
          isOpen={showEmployeeManagement}
          onClose={() => setShowEmployeeManagement(false)}
          onUpdate={refreshDashboardData}
        />
      </div>
    );
  }

  // Default fallback (if role unknown)
  return (
    <div className="pt-16 min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto p-6">
        <Card className="bg-gray-900/50 border-violet-500/20">
          <CardContent className="p-8 text-center">
            <h2 className="text-white text-xl font-semibold mb-2">Welcome</h2>
            <p className="text-gray-400">Select a section from the navigation to get started.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;